/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useState } from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import {
  Alert, Badge, Card, CardMedia, Link, Typography
} from '@one-thd/sui-atomic-components';
import { Paint, CounterTop, Drill } from '@one-thd/sui-icons';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { ShareProject } from '@thd-olt-component-react/project-forms';
import { useImpression } from '@thd-olt-component-react/impression';
import { ProjectTilePlaceholder } from '../project-tile/ProjectTilePlaceholder';
import { useMatchMedia } from '../../hooks/useMatchMedia';
import { triggerAnalytics } from '../../helpers';

/**
 * ProjectCardList - Presentational project card list component
 * Displays a single project card with project information and up to 3 thumbnails
 * The consuming component controls layout, sizing, and arrangement (row/column/grid)
 */
const {
  func, shape, string, arrayOf, bool, number
} = proptypes;

const ProTile = ({
  project,
  projectId,
  position,
  maxThumbnails,
  loading,
  impressionName,
  isDashboardSnapshot
}) => {
  const [message, setMessage] = useState({ text: '', type: '' });

  const { ref, clickID } = useImpression({
    data: {
      id: projectId || '',
      name: impressionName || 'ProjectsOverview',
      component: impressionName || 'ProjectsOverview',
      position: position || 0,
      category: project?.status || '',
      type: 'content'
    }
  });

  const handleAlert = useCallback((text, type) => setMessage({ text, type }), []);

  // Show placeholder when loading and no project data
  if (!project && loading) return <ProjectTilePlaceholder />;
  if (!project) return null;

  const {
    id,
    projectName,
    status,
    createdByName,
    images = [],
    numberOfItems = 0
  } = project;

  const url = `/projects/${id}`;
  const isActive = status === 'active';
  const statusInfo = {
    text: isActive ? 'Active' : 'Archived',
    color: isActive ? 'info' : 'medium',
    textColor: isActive ? 'primary' : 'subtle', // Active uses primary text, Archived uses subtle text
  };

  // Display only the number of thumbnails specified (default 3 for UI specification)
  const displayImages = images?.slice(0, maxThumbnails) || [];
  const remainingCount = numberOfItems - displayImages.length;

  const handleAddMaterialsClick = () => {
    const payload = {
      action: 'button-click',
      id: projectId || '',
      component: 'empty project add materials gray box click',
    };
    triggerAnalytics('projects.interaction', payload);
    window.location.href = url;
  };

  return (
    <div
      ref={ref}
      // eslint-disable-next-line react/no-unknown-property
      clickid={clickID}
      className="sui-h-full"
    >
      <Card
        className={classNames(
          'sui-w-full sui-h-full [&>div]:sui-justify-between [&>div]:sui-h-full',
          { 'sui-rounded-md': isDashboardSnapshot }
        )}
        data-component="ProjectCard"
        data-testid="project-card"
      >
        <div className="sui-flex sui-flex-col sui-gap-4">
          {/* Header section with badge and share icon */}
          <div className="sui-flex sui-justify-between">
            <Badge size="small" variant="subtle" color={statusInfo.color}>
              <span className="sui-capitalize">Status: {statusInfo.text}</span>
            </Badge>
            <div className="sui-flex sui-items-center">
              <QueryProvider cacheKey={`share-project-${id}`}>
                <ShareProject
                  project={{
                    id,
                    defaultListId: project.defaultListId,
                    name: projectName,
                    public: project.public,
                    itemGroups: project.itemGroups
                  }}
                  showLink
                  iconSize="small"
                  onSuccessMessage={(msg) => handleAlert(msg, 'success')}
                  onFailureMessage={(error) => handleAlert(error, 'error')}
                />
              </QueryProvider>
            </div>
          </div>

          {/* Project Details Section */}
          <div className="sui-flex sui-flex-col sui-gap-0.5">
            {/* Project Title */}
            <div className="sui-flex sui-items-start sui-gap-2">
              <Typography variant="body" weight="bold" color={statusInfo.textColor} className="sui-shrink-0">
                Project:
              </Typography>
              <Link href={url} className="sui-underline">
                <Typography variant="body" color={statusInfo.textColor} lineClamp="2">
                  {projectName}
                </Typography>
              </Link>
            </div>

            {message.text && (
              <div className="sui-my-1">
                <Alert
                  status={message.type === 'success' ? 'info' : 'error'}
                  onClose={() => setMessage({ text: '', type: '' })}
                >
                  {message.text}
                </Alert>
              </div>
            )}

            {/* Created By - Only show if createdByName exists */}
            {createdByName && (
              <div className="sui-flex sui-items-start sui-gap-2 sui-pt-1">
                <Typography variant="body" weight="bold" color={statusInfo.textColor} className="sui-shrink-0">
                  Created By:
                </Typography>
                <Typography variant="body" color={statusInfo.textColor}>
                  {createdByName}
                </Typography>
              </div>
            )}
          </div>
        </div>

        {/*
          Project Materials Section. The two states lay out differently on purpose:
          - Populated: content-sized, so CardContent's justify-between (see Card's className)
            pins the thumbnails to the bottom of the card. justify-between only supplies a gap
            when the card has leftover height — sui-mt-4 guarantees a minimum gap even when
            the header block fills the card on its own.
          - Empty: sui-flex-1 makes this section consume any leftover card height so the
            "Add materials" panel below can fill it via its own sui-h-full, instead of sitting
            at its own content height with dead space above it whenever the card is taller
            than its natural content (e.g. Dashboard Snapshot's shared height floor across
            tabs). No margin: CardContent's own sui-gap-4 already spaces it from the header,
            matching the "Expand Add Materials Panel" comps.
        */}
        <div
          className={classNames('sui-flex sui-flex-col sui-gap-1', {
            'sui-mt-4': displayImages.length > 0,
            'sui-flex-1': displayImages.length === 0
          })}
        >
          {displayImages.length > 0 ? (
            <>
              <Typography variant="body" weight="bold" color={statusInfo.textColor}>
                Project Materials:
              </Typography>
              {/* Thumbnails - Show up to 3 images plus count */}
              <div className="sui-flex sui-gap-3 sui-items-center sui-mt-1">
                {displayImages.map((imageUrl, index) => (
                  <div
                    key={`thumbnail-${index}`}
                    className="sui-w-14 sui-h-14
                  sui-flex sui-items-center sui-justify-center
                  sui-overflow-hidden sui-rounded-sm"
                  >
                    <CardMedia
                      src={imageUrl}
                      alt={`Product ${index + 1}`}
                      className="sui-w-full sui-h-full sui-object-cover"
                    />
                  </div>
                ))}

                {/* Show remaining count if there are more items */}
                {remainingCount > 0 && (
                  <div
                    className="sui-w-14 sui-h-14 sui-flex sui-items-center
                    sui-justify-center sui-rounded-md sui-border-1 sui-border-solid sui-border-primary"
                  >
                    <Typography variant="body-base" color="subtle">
                      +{remainingCount}
                    </Typography>
                  </div>
                )}
              </div>
            </>
          ) : (
            /*
              Empty state - show "Add materials to your project" message with icons.
              The vertical padding (py-5 / @sm:py-12) carries the 16px this state leaves off the
              wrapper's sui-mt-4, so the empty card keeps the same natural height: a row of
              equal-height carousel cards doesn't shrink (and shift its populated neighbors) when
              an empty card is the tallest. Invisible once the panel fills a taller card, since its
              content is centered.
            */
            <div
              className="sui-flex sui-items-center sui-h-full
            sui-justify-between sui-gap-4 sui-bg-subtle
            sui-rounded-md sui-px-3 sui-py-5 @sm:sui-py-12"
            >
              <Link component="button" onClick={handleAddMaterialsClick} variant="body-sm" color="medium" align="left">
                Add materials to your project
              </Link>
              <div className="sui-flex sui-gap-4 sui-items-center">
                <Drill size="regular" color="medium" />
                <CounterTop size="regular" color="medium" />
                <Paint size="regular" color="medium" />
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

ProTile.displayName = 'ProTile';

ProTile.propTypes = {
  projectId: string.isRequired,
  project: shape({
    id: string,
    projectName: string,
    defaultListId: string,
    public: bool,
    status: string,
    modifyDateTime: string,
    createdBy: string,
    createdByName: string,
    isCreatedByTHDAssociate: bool,
    numberOfItems: number,
    images: arrayOf(string)
  }),
  position: number,
  loading: bool,
  maxThumbnails: number,
  impressionName: string,
  /** Set true when rendering inside Dashboard Snapshot, to use its 4px corner radius. */
  isDashboardSnapshot: bool
};

ProTile.defaultProps = {
  project: null,
  maxThumbnails: 3,
  loading: false,
  position: 0,
  impressionName: 'ProjectsOverview',
  isDashboardSnapshot: false
};

export { ProTile };
